import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import Table from "./../Theme/Table.js";
import Spinner from "../Spinner";
import { useContext } from "react";
import { RoleContext } from "../RoleContext";
import ActionButtons from "../ActionButton.js";

const TripsList = ({ socket }) => {
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);
  const userType = typeof window !== "undefined" ? localStorage.getItem("userType") : null;
  const isCompanyUser = userType === "ambulance_company";
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    tripId: null,
    isEmergency: false,
    estimatedPrice: 0,
  });
  const [manualPriceEnabled, setManualPriceEnabled] = useState(false);
  const [manualPrice, setManualPrice] = useState("");
  const [endReason, setEndReason] = useState("");
  const navigate = useNavigate();




  const getTrips = async () => {
    try {
      const response = await axiosInstance.get("activeTrips/");
      setTrips(response.data);
      setLoading(false);
    } catch (err) {
      toast.error("Something went wrong ...");
    }
  };

  const getTripEndDetails = async (tripId) => {
    try {
      const response = await axiosInstance.get(`/tripEndDetails/${tripId}`);
      console.log("========tripEndDetails=========",response.data);
      return response.data;
    } catch (error) {
      throw new Error("Failed to get trip details");
    }
  };

  const handleEndTrip = async () => {
    try {
      // Validate reason
      if (!endReason || endReason.trim() === "") {
        toast.error("Please provide a reason for ending this trip");
        return;
      }

      // Validate manual price if enabled
      if (manualPriceEnabled) {
        const priceValue = parseFloat(manualPrice);
        if (!manualPrice || isNaN(priceValue) || priceValue <= 0) {
          toast.error("Please enter a valid manual price greater than 0");
          return;
        }
      }

      const tripDetails = await getTripEndDetails(confirmDialog.tripId);

      console.log("====ending the trip=====Trip Details======:", tripDetails);
      if (confirmDialog.isEmergency) {
         const emergencyData = {
          formCompleted: true,
          tripId: tripDetails.tripId,
          requestId: tripDetails.tripRequestId,
          manualPrice,
          endReason: endReason.trim()
        };
        
        // Add manual price if enabled
        if (manualPriceEnabled && manualPrice) {
          emergencyData.manualPrice = parseFloat(manualPrice);
        }
        
        socket.emit('endEmergencyTrip', emergencyData);

      }else{

      
      // Emit end event with required data
      const endData = {
        tripId: tripDetails.tripId,
        tripRequestId: tripDetails.tripRequestId,
        reservationRequestId: tripDetails.reservationRequestId,
        destinationLocationLatitude: tripDetails.destinationLocationLatitude,
        destinationLocationLongitude: tripDetails.destinationLocationLongitude,
        pickUpLocationLatitude: tripDetails.pickUpLocationLatitude,
        pickUpLocationLongitude: tripDetails.pickUpLocationLongitude,
        code: tripDetails.verificationCode,
        endReason: endReason.trim()
      };

      if (manualPriceEnabled && manualPrice) {
        endData.manualPrice = parseFloat(manualPrice);
      }

      // Reservation trips use a separate socket event
      if (tripDetails.isReservation) {
        socket.emit('endReservation', JSON.stringify(endData));
      } else {
        socket.emit('end', JSON.stringify(endData));
      }
    }

      setConfirmDialog({ open: false, tripId: null });
      setManualPriceEnabled(false);
      setManualPrice("");
      setEndReason("");
      getTrips(); // Refresh trips list
    } catch (error) {
      toast.error("Failed to end trip");
    }
  };

  useEffect(() => {
    getTrips();
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Passenger Information",
        columns: [
          {
            Header: "ID",
            accessor: "id",
          },
          {
            Header: "First Name",
            accessor: "passenger.firstName",
          },
          {
            Header: "Last Name",
            accessor: "passenger.lastName",
          },
          {
            Header: "Phone Number",
            accessor: "passenger.phoneNumber",
          },
        ],
      },
      {
        Header: "Driver Information",
        columns: [
          {
            Header: "First Name",
            accessor: "driver.firstName",
          },
          {
            Header: "Last Name",
            accessor: "driver.lastName",
          },
          {
            Header: "Phone Number",
            accessor: "driver.phoneNumber",
          },
          ...(!isCompanyUser
            ? [
                {
                  Header: "End Trip",
                  accessor: "endTrip",
                  disableFilters: true,
                  Cell: (row) => {
                    const tripId = row.cell.row.original.id;
                    const status = row.cell.row.original.status;
                    const isEmergency = row.cell.row.original.isEmergency || false;

                    const estimatedPrice = row.cell.row.original.estimatedPrice || 0;

                    return status === "Active" ? (
                      <button
                        onClick={() => {
                          setConfirmDialog({ open: true, tripId, isEmergency, estimatedPrice });
                          setManualPrice(estimatedPrice); // Pre-fill with estimated price
                        }}
                        style={{
                          backgroundColor: "#1e40af",
                          color: "white",
                          padding: "8px 16px",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "500",
                          transition: "background-color 0.2s",
                          whiteSpace: "nowrap",
                          minWidth: "100px",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          "&:hover": {
                            backgroundColor: "#1e3a8a",
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        End Trip
                      </button>
                    ) : (
                      <span style={{ color: "gray" }}>Ended</span>
                    );
                  },
                },
              ]
            : []),
          {
            "Header": "isEmergency",
            "accessor": "isEmergency",
            "disableFilters": true,
            "Cell": (row) => {
              const isEmergency = row.cell.row.original.isEmergency || false;
              return isEmergency ? "Yes" : "No";
            }
          },
          {
            Header: "Status",
            accessor: "status",
            disableFilters: true,
            Cell: (row) => {
              const status = row.cell.row.original.status || "Active";
              return (
                <Chip 
                  label={status} 
                  color={status === "Active" ? "success" : "warning"}
                  variant="filled"
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: status === "Active" 
                      ? '0 2px 4px rgba(0,255,0,0.2)' 
                      : '0 2px 4px rgba(255,165,0,0.2)'
                  }}
                />
              );
            },
          },
          {
            Header: "Action",
            accessor: "action",
            disableFilters: true,
            Cell: (row) => (
              <div>
                {role.filter((e) => e.perm_name === "list_active_trips").length > 0 && (
                  <Tooltip title="View active_trips">
                    <span>
                      <VisibilityIcon
                        color="success"
                        sx={{
                          cursor: "pointer",
                          transition: "transform 0.2s",
                          "&:hover": {
                            transform: "scale(1.2)",
                            color: "#16a34a",
                          },
                        }}
                        onClick={() => navigate(`/tripDetail/${row.cell.row.original.id}`)}
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ],
      },
    ],
    [isCompanyUser]
  );

  return (
    <div className="content-wrappers" style={{ marginTop: "-40px" }}>
      <div className="content-wrappers">
        <br />
        <br />
        <ToastContainer />

        {!isCompanyUser && (
          <Dialog
            open={confirmDialog.open}
            onClose={() => {
              setConfirmDialog({ open: false, tripId: null });
              setManualPriceEnabled(false);
              setManualPrice("");
              setEndReason("");
            }}
            PaperProps={{
              style: {
                borderRadius: "12px",
                padding: "16px",
                maxWidth: "450px",
              },
            }}
          >
            <DialogTitle
              sx={{
                textAlign: "center",
                pb: 1,
                "& .MuiTypography-root": {
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#1e40af",
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                <WarningIcon sx={{ color: "#1e40af", fontSize: 28 }} />
                <Typography>End Trip Confirmation</Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ pb: 3 }}>
              <Typography
                align="center"
                sx={{
                  fontSize: "1rem",
                  color: "#4b5563",
                  mt: 1,
                  mb: 2,
                }}
              >
                Are you sure you want to end this trip? This action cannot be undone.
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: "600", mb: 1, color: "#374151" }}>
                  Reason for Ending Trip <span style={{ color: "#ef4444" }}>*</span>
                </Typography>
                <textarea
                  placeholder="e.g., Trip completed successfully, GPS tracking failed, etc."
                  value={endReason}
                  onChange={(e) => setEndReason(e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: endReason.trim() ? "2px solid #10b981" : "2px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    outline: "none",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                  onBlur={(e) => (e.target.style.borderColor = endReason.trim() ? "#10b981" : "#d1d5db")}
                />
                {!endReason.trim() && (
                  <Typography sx={{ fontSize: "0.75rem", color: "#ef4444", mt: 0.5 }}>
                    This field is required
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  p: 2,
                  backgroundColor: "#eff6ff",
                  borderRadius: "8px",
                  border: "1px solid #bfdbfe",
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", color: "#1e40af", fontWeight: "600", mb: 0.5 }}>
                  Estimated Price
                </Typography>
                <Typography sx={{ fontSize: "1.5rem", color: "#1e3a8a", fontWeight: "bold" }}>
                  {confirmDialog.estimatedPrice
                    ? `${parseFloat(confirmDialog.estimatedPrice).toFixed(2)} Birr`
                    : "N/A"}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.5 }}>
                  This will be used as actual price if calculated automatically
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#fef3c7",
                  borderRadius: "8px",
                  border: "1px solid #fcd34d",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <input
                    type="checkbox"
                    id="manualPriceCheckbox"
                    checked={manualPriceEnabled}
                    onChange={(e) => {
                      setManualPriceEnabled(e.target.checked);
                      if (!e.target.checked) setManualPrice("");
                    }}
                    style={{ marginRight: "8px", cursor: "pointer" }}
                  />
                  <label
                    htmlFor="manualPriceCheckbox"
                    style={{ cursor: "pointer", fontWeight: "600", fontSize: "0.9rem", color: "#92400e" }}
                  >
                    Override with Manual Price
                  </label>
                </Box>

                <Typography sx={{ fontSize: "0.75rem", color: "#78350f", mt: 1, mb: 1.5 }}>
                  ⚠️ Use this only when GPS tracking failed or driver app crashed
                </Typography>

                {manualPriceEnabled && (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: "0.8rem", color: "#78350f", fontWeight: "600", mb: 0.5 }}>
                      Manual Price (Birr)
                    </Typography>
                    <input
                      type="number"
                      placeholder="Enter price in Birr"
                      value={manualPrice}
                      onChange={(e) => setManualPrice(e.target.value)}
                      min="0"
                      step="0.01"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "2px solid #fbbf24",
                        borderRadius: "6px",
                        fontSize: "1rem",
                        fontWeight: "600",
                        outline: "none",
                        backgroundColor: "white",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#f59e0b")}
                      onBlur={(e) => (e.target.style.borderColor = "#fbbf24")}
                    />
                    <Typography sx={{ fontSize: "0.75rem", color: "#78350f", mt: 0.5, fontStyle: "italic" }}>
                      💡 Pre-filled with estimated price - adjust if needed
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                padding: "16px",
                borderTop: "1px solid #e5e7eb",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Button
                onClick={() => {
                  setConfirmDialog({ open: false, tripId: null });
                  setManualPriceEnabled(false);
                  setManualPrice("");
                  setEndReason("");
                }}
                sx={{
                  color: "#6b7280",
                  backgroundColor: "#f3f4f6",
                  padding: "8px 24px",
                  "&:hover": {
                    backgroundColor: "#e5e7eb",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEndTrip}
                sx={{
                  backgroundColor: "#1e40af",
                  color: "white",
                  padding: "8px 24px",
                  "&:hover": {
                    backgroundColor: "#1e3a8a",
                  },
                }}
              >
                End Trip
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <ActionButtons title="Active Trips" is_all_trips={false} />
                  <div className="card-body">
                    {loading ? (
                      <div>
                        <Spinner />
                      </div>
                    ) : (
                      <Table columns={columns} data={trips} edit="trip" detail="viewTrip" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TripsList;